import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../../common/SafeIcon';

const { FiClock, FiArrowLeft, FiActivity } = FiIcons;
const { BsHourglassSplit, BsShieldCheck } = BsIcons;

const CognitiveLongevity = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Link to="/resources" className="inline-flex items-center text-slate-500 hover:text-indigo-600 mb-8 transition-colors font-medium">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Library
        </Link>

        <header className="mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wider">
              Longevity
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
              Cognitive Longevity in 2025: The New Rules for a Sharper Mind at Any Age
            </h1>
            <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                SV
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-slate-900">Sarah Vance</span>
                <span className="text-xs text-slate-500">10 min read</span>
              </div>
            </div>
          </motion.div>
        </header>

        <div className="rounded-3xl overflow-hidden mb-12 shadow-xl">
          <img 
            src="https://cdn1.sharemyimage.com/smi/2026/01/01/doctor-from-future-concept.jpg" 
            alt="Future of Brain Health" 
            className="w-full object-cover"
          />
        </div>

        <div className="prose prose-lg prose-slate max-w-none">
          <p className="lead text-xl text-slate-600 mb-8">
            The old model of aging assumed that cognitive decline was inevitable—a slow, steady slide into forgetfulness. The new model, backed by 2025 neuroscience, says otherwise: <strong>Your brain is capable of remodeling itself until the day you die.</strong>
          </p>

          <h3>From "Anti-Aging" to "Pro-Longevity"</h3>
          <p>
            We are seeing a cultural shift. We aren't just trying to stop the clock; we are trying to optimize the machine. Cognitive longevity isn't about doing crossword puzzles; it's about systemic health.
          </p>

          <h3>The Myth of "Fixed" Neurons</h3>
          <p>
            For decades, dogma stated that you were born with a set number of brain cells, and that was it. We now know about <strong>Neurogenesis</strong>—the birth of new neurons, primarily in the hippocampus. This process continues well into your 80s and 90s, provided the environment is right.
          </p>

          <h3>The 2025 Protocol for Brain Health</h3>
          <p>
            Top researchers are now converging on a "multi-modal" approach to brain health. It's not just one thing; it's a lifestyle ecosystem.
          </p>

          <div className="space-y-8 my-12">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-900 font-bold text-xl">1</div>
              <div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Metabolic Flexibility</h4>
                <p className="text-slate-600">Your brain is an energy hog, consuming 20% of your calories. Insulin resistance (often called Type 3 Diabetes) starves the brain. Keeping blood sugar stable is perhaps the single most impactful thing you can do for long-term memory.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-900 font-bold text-xl">2</div>
              <div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Novelty & Complexity</h4>
                <p className="text-slate-600">Routine is the enemy of neuroplasticity. The brain thrives on newness. Learning a language, a musical instrument, or even a new dance step forces the brain to wire new connections. Passive entertainment (TV) does not count.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-900 font-bold text-xl">3</div>
              <div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Stress Calibration</h4>
                <p className="text-slate-600">Not all stress is bad, but chronic, low-grade anxiety is neurotoxic. Tools that help regulate the autonomic nervous system—like breathwork or <Link to="/review/the-brain-song" className="text-indigo-600 underline">audio entrainment</Link>—are becoming essential hygiene, just like brushing your teeth.</p>
              </div>
            </div>
          </div>

          <h3>The Role of Technology</h3>
          <p>
            We used to fear technology would rot our brains. Now, we are learning to use it to save them. From bio-wearables that track sleep quality to audio programs that stimulate gamma waves, technology is allowing us to peek under the hood and fine-tune our cognitive engines.
          </p>
          <p>
            The future belongs to those who take active stewardship of their minds. It's not about being a genius; it's about being resilient, adaptable, and sharp, no matter the year on the calendar.
          </p>
        </div>
      </article>
    </div>
  );
};

export default CognitiveLongevity;